import { Button } from "@/components/ui/button";
import { ExternalLink, Headphones, Mic, Play, Youtube } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import podcastHero from "@/assets/podcast-hero.png";
import podcastBackground from "@/assets/podcast-background.jpg";

const Podcast = () => {
  const youtubeChannel = "https://youtube.com/@mohamed-shoukry.podcast?si=Bhf4H4AyBYFggxb4";

  return (
    <div className="min-h-screen bg-background">
      <Header showSearch={false} />
      
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        {/* Full-size Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${podcastBackground})` }}
        >
          <div className="absolute inset-0 bg-black/60" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex items-center gap-2 mb-4">
            <Mic className="h-5 w-5 text-white/80" />
            <span className="text-white/80 text-sm font-medium uppercase tracking-wider">
              FTS Presents
            </span>
          </div>
          
          <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
            FASTTRACK
            <br />
            PODCAST
          </h1>
          
          <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-lg font-light">
            Sharing experience to make impact. Join us for insightful conversations about project management, leadership, and career growth.
          </p>

          <div className="flex flex-wrap gap-4">
            <Button 
              size="lg"
              className="bg-white/10 border border-white/50 text-white hover:bg-white/20 gap-2"
              onClick={() => window.open(youtubeChannel, '_blank')}
            >
              <Youtube className="h-5 w-5" />
              Watch on YouTube
            </Button>
            <Button 
              size="lg"
              className="bg-white/10 border border-white/50 text-white hover:bg-white/20 gap-2"
              onClick={() => window.open(youtubeChannel, '_blank')}
            >
              <Play className="h-5 w-5" />
              Latest Episodes
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-3xl lg:text-4xl font-bold text-foreground mb-4">
              What You'll Learn
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Deep dive into project management topics with real-world insights and expert advice.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Headphones,
                title: "Expert Interviews",
                description: "Conversations with industry leaders sharing their experiences and best practices."
              },
              {
                icon: Mic,
                title: "PM Deep Dives",
                description: "In-depth discussions on project management methodologies, tools, and techniques."
              },
              {
                icon: Play,
                title: "Career Insights",
                description: "Tips and strategies for advancing your project management career."
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="bg-card border border-border rounded-xl p-6 text-center hover:shadow-lg transition-shadow"
              >
                <div className="w-14 h-14 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="h-7 w-7 text-destructive" />
                </div>
                <h3 className="font-semibold text-lg text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Host Bio Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="aspect-square max-w-md mx-auto lg:mx-0 overflow-hidden rounded-2xl">
                <img
                  src={podcastHero}
                  alt="Mohamed Shoukry - Podcast Host"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-destructive rounded-full flex items-center justify-center">
                <Mic className="h-10 w-10 text-destructive-foreground" />
              </div>
            </div>

            <div>
              <span className="text-destructive font-medium text-sm uppercase tracking-wider mb-2 block">
                Your Host
              </span>
              <h2 className="font-playfair text-3xl lg:text-4xl font-bold text-foreground mb-4">
                Mohamed Shoukry
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                With over 13 years of experience in project management and leadership, Mohamed brings a wealth of knowledge and real-world insights to every episode. His passion for sharing experience and making an impact drives the Fasttrack Podcast.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                As the founder of FTS Consulting, Mohamed has helped countless organizations transform their project delivery capabilities and develop world-class project managers.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="px-4 py-2 bg-destructive/10 text-destructive rounded-full text-sm font-medium">
                  PMP Certified
                </span>
                <span className="px-4 py-2 bg-destructive/10 text-destructive rounded-full text-sm font-medium">
                  Agile Expert
                </span>
                <span className="px-4 py-2 bg-destructive/10 text-destructive rounded-full text-sm font-medium">
                  Leadership Coach
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Episodes Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Featured Episodes
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Watch our latest episodes and dive deep into project management insights.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              { 
                id: "xm1Vp9pVhZw", 
                title: "كيف تخطط مشروعك في دقائق باستخدام الذكاء الاصطناعي", 
                description: "تعرف على كيفية استخدام أدوات الذكاء الاصطناعي لتخطيط مشاريعك بشكل أسرع وأكثر كفاءة.",
                isNew: true
              },
              { 
                id: "klR7XY51Nsg", 
                title: "أسرع طريق للتحول الرقمي في الإنشاءات باستخدام Power Apps", 
                description: "تعرف على كيفية استخدام Power Apps لتسريع التحول الرقمي في قطاع الإنشاءات وتحسين كفاءة إدارة المشاريع.",
                isNew: false
              },
              { 
                id: "wtmr3PuIDHM", 
                title: "Project Management Excellence", 
                description: "Discover the key principles that separate good project managers from great ones. Learn practical strategies for leading successful projects."
              },
              { 
                id: "mf_zyd2EuS4", 
                title: "Leadership & Career Growth", 
                description: "Insights on advancing your career in project management. Tips for building influence and becoming a respected leader in your organization."
              },
              { 
                id: "N0bhvD-rnbI", 
                title: "Agile Methodologies Deep Dive", 
                description: "Exploring agile frameworks and how to implement them effectively. Real-world examples from successful agile transformations."
              }
            ].map((video, index) => (
              <div 
                key={index}
                className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-shadow group relative"
              >
                {video.isNew && (
                  <div className="absolute top-3 right-3 z-10 bg-destructive text-destructive-foreground text-xs font-bold px-2 py-1 rounded">
                    NEW
                  </div>
                )}
                <div className="aspect-video">
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${video.id}`}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-semibold text-lg text-foreground mb-2 group-hover:text-destructive transition-colors">
                    {video.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {video.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Button 
              variant="outline"
              size="lg"
              className="gap-2"
              onClick={() => window.open(youtubeChannel, '_blank')}
            >
              <Youtube className="h-5 w-5" />
              View All Episodes
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-r from-destructive to-destructive/80 rounded-2xl p-8 lg:p-12">
            <Youtube className="h-12 w-12 text-destructive-foreground mx-auto mb-6" />
            <h2 className="font-playfair text-2xl lg:text-3xl font-bold text-destructive-foreground mb-4">
              Subscribe to Our Channel
            </h2>
            <p className="text-destructive-foreground/90 mb-8 max-w-xl mx-auto">
              Never miss an episode. Subscribe now and join thousands of project managers on their journey to excellence.
            </p>
            <Button 
              size="lg"
              className="bg-background text-foreground hover:bg-background/90 gap-2"
              onClick={() => window.open(youtubeChannel, '_blank')}
            >
              <ExternalLink className="h-5 w-5" />
              Visit YouTube Channel
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Podcast;
